
import { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Ayden from '../../images/bigAyden.jpg';
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon as CheckCircleIconOutline,
  SparklesIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import logger from '../../utils/logger';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  let navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    // Load remembered email if exists
    const rememberedEmail = localStorage.getItem('ayden_remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    const auth = getAuth();
    try {
      await signInWithEmailAndPassword(auth, email, password);

      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('ayden_remembered_email', email);
      } else {
        localStorage.removeItem('ayden_remembered_email');
      }

      navigate('/');
    } catch (error) {
      logger.error('Sign In Error:', error);
      let errorMessage = 'Invalid email or password. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess(false);

    const auth = getAuth();

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
      logger.info('Password reset email sent to:', resetEmail);

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (error) {
      logger.error('Password Reset Error:', error);
      let errorMessage = 'Failed to send reset email. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      }

      setResetError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const features = [
    { icon: WrenchScrewdriverIcon, text: 'Complete Job Management' },
    { icon: TruckIcon, text: 'Parts & Procurement' },
    { icon: UserGroupIcon, text: 'Customer Relations' },
    { icon: ClockIcon, text: 'Real-time Tracking' }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className={`w-full max-w-6xl mx-4 transition-all duration-1000 transform ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 flex flex-col lg:flex-row">

              {/* Left Side - Branding & Info */}
              <div className="lg:w-1/2 relative bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 p-6 flex flex-col justify-between overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full -ml-48 -mt-48"></div>

                <div className="relative z-10">
                  {/* Logo Section */}
                  <div className="mb-2">
                    <div className="inline-block p-2 bg-white/10 rounded-2xl backdrop-blur-sm mb-2 transform transition-transform duration-300 hover:scale-105">
                      <img
                        className="h-20 w-auto rounded-xl shadow-2xl"
                        src={Ayden}
                        alt="Ayden Garage"
                      />
                    </div>
                    <h1 className="text-4xl font-black text-white mb-2 tracking-tight leading-tight">
                    <span className="text-3xl bg-gradient-to-r from-yellow-200 to-yellow-400 bg-clip-text text-transparent">
                       Garage Management System
                      </span>
                    </h1>
                    <p className="text-base text-indigo-100 font-medium leading-relaxed max-w-md">
                      Your complete solution for automotive service excellence, streamlining operations from customer intake to final delivery.
                    </p>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 gap-3 mb-4">
                    {features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 transition-all duration-300 hover:bg-white/20 hover:transform hover:translate-x-2"
                      >
                        <div className="flex-shrink-0 w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                          <feature.icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-white font-semibold text-sm">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <div className="text-center">
                      <div className="text-2xl font-black text-white mb-1">99.9%</div>
                      <div className="text-xs text-indigo-200 font-medium">Uptime</div>
                    </div>
                    <div className="text-center border-x border-white/20">
                      <div className="text-2xl font-black text-white mb-1">24/7</div>
                      <div className="text-xs text-indigo-200 font-medium">Support</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-black text-white mb-1">Secure</div>
                      <div className="text-xs text-indigo-200 font-medium">256-bit SSL</div>
                    </div>
                  </div>
                </div>

                {/* Bottom Badge */}
                <div className="relative z-10 mt-4">
                  <div className="flex items-center gap-2 text-white/80">
                    <SparklesIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">Trusted by automotive professionals</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Login Form */}
              <div className="lg:w-1/2 p-6 flex flex-col justify-center">
                <div className="w-full max-w-md mx-auto">
                  {/* Form Header */}
                  <div className="mb-6">
                    <h2 className="text-3xl font-black text-gray-900 mb-1">
                      Welcome Back
                    </h2>
                    <p className="text-gray-600 font-medium text-sm">
                      Sign in to access your workspace
                    </p>
                  </div>

                  <form className="space-y-4" onSubmit={onSubmit}>
                    {/* Error Message */}
                    {error && (
                      <div className="p-3 rounded-xl bg-red-50 border-l-4 border-red-500 animate-shake">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-semibold text-red-800">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Email Input */}
                    <div>
                      <label htmlFor="email-address" className="block text-sm font-bold text-gray-700 mb-1.5">
                        Email Address
                      </label>
                      <div className="relative group">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors duration-200 ${emailFocused ? 'text-indigo-500' : 'text-gray-400'}`}>
                          <EnvelopeIcon className="h-5 w-5" />
                        </div>
                        <input
                          id="email-address"
                          name="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setEmailFocused(true)}
                          onBlur={() => setEmailFocused(false)}
                          required
                          className="block w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm font-medium hover:border-gray-300"
                          placeholder="you@example.com"
                          autoComplete="email"
                        />
                      </div>
                    </div>

                    {/* Password Input */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-1.5">
                        Password
                      </label>
                      <div className="relative group">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors duration-200 ${passwordFocused ? 'text-indigo-500' : 'text-gray-400'}`}>
                          <LockClosedIcon className="h-5 w-5" />
                        </div>
                        <input
                          id="password"
                          value={password}
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={() => setPasswordFocused(false)}
                          autoComplete="current-password"
                          required
                          className="block w-full pl-11 pr-12 py-3 rounded-xl border-2 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm font-medium hover:border-gray-300"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-indigo-600 transition-colors duration-200 focus:outline-none"
                          tabIndex="-1"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer transition-colors duration-200"
                        />
                        <label htmlFor="remember-me" className="ml-2 block text-sm font-medium text-gray-700 cursor-pointer select-none">
                          Remember me
                        </label>
                      </div>

                      <div className="text-sm">
                        <button
                          type="button"
                          onClick={() => {
                            setShowResetModal(true);
                            setResetEmail(email);
                          }}
                          className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors duration-200 focus:outline-none focus:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="group relative flex w-full justify-center items-center gap-3 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-size-200 bg-pos-0 px-4 py-3 text-base font-bold text-white hover:bg-pos-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-pos-0"
                      >
                        {isLoading ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>Signing In...</span>
                          </>
                        ) : (
                          <>
                            <span>Login</span>
                            <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Security Badge */}
                  <div className="mt-6">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-white text-gray-500 font-semibold flex items-center gap-2">
                          <ShieldCheckIcon className="h-4 w-4 text-green-500" />
                          Secure Access
                        </span>
                      </div>
                    </div>

                    {/* Trust Indicators */}
                    <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                      <div className="flex flex-col items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mb-1" />
                        <span className="text-xs font-semibold text-gray-600">SSL Secured</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mb-1" />
                        <span className="text-xs font-semibold text-gray-600">Encrypted</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mb-1" />
                        <span className="text-xs font-semibold text-gray-600">Protected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-slideUp">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
                <LockClosedIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h3>
              <p className="text-sm text-gray-600">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {resetSuccess ? (
              <div className="text-center py-4">
                <CheckCircleIconOutline className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">Check Your Email!</p>
                <p className="text-sm text-gray-600">
                  We've sent password reset instructions to <strong>{resetEmail}</strong>
                </p>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                {resetError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm font-medium text-red-800">{resetError}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="reset-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="block w-full px-4 py-3 rounded-lg border-2 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetModal(false);
                      setResetEmail('');
                      setResetError('');
                    }}
                    className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1 px-4 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {resetLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Custom Styles for Animations */}
      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-4px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(4px);
          }
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .bg-size-200 {
          background-size: 200% auto;
        }

        .bg-pos-0 {
          background-position: 0% center;
        }

        .bg-pos-100 {
          background-position: 100% center;
        }
      `}</style>
    </>
  );
}